import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import mapboxgl from 'mapbox-gl';
import {
  EARTH_ZOOM,
  mapModes,
  SOIL_BOUNDARIES_LAYER_ID,
} from '../../constants/map';
import { CONUS_CENTER } from '../../constants/coords';
import { MapContext } from '../../contexts/MapContext';
import useDraw from '../../hooks/useDraw';
import useDrawLayer from '../../hooks/useDrawLayer';
import useOffset from '../../hooks/useOffset';
import useRotate from '../../hooks/useRotate';
import { FarmQuery } from '../../queries';
import { useQueryWithFarmId } from '../../utils/hooks';
import {
  addImageToMap,
  addReferenceLayers,
  setAttributionControl,
  toggleFullscreen,
} from '../../utils/map';
import UserLayer from '../map/UserLayer';
import FieldSectionLayers from '../map/FieldSectionLayers';
import TilesetLayer from '../map/TilesetLayer';
import NaipLayer from '../map/NaipLayer';
import AzureLayer from '../map/AzureLayer';
import NoSatelliteLayer from '../map/NoSatelliteLayer';
import ContourLayer from '../map/ContourLayer';
import BaseMap from '../map/BaseMap';
import { getRootUrl } from '../../utils/utils';
import CursorLatLng from '../map/CursorLatLng';
import Shortcut from '../basics/Shortcut';
import { SHORTCUTS } from '../../constants/shortcuts';
import MapContainer from '../map/MapContainer';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import useMeasurement from '../../hooks/useMeasurement';
import useColorSoilLayer from '../../hooks/useColorSoilLayer';

const onMapLoad = (attributionControlRef) => (mapRef) => {
  addReferenceLayers(mapRef.current);
  // Add rotate and offset icons
  const rootUrl = getRootUrl();
  addImageToMap(mapRef.current, 'rotate', `${rootUrl}/images/Rotate.png`);
  addImageToMap(mapRef.current, 'offset', `${rootUrl}/images/Offset.png`);
  addImageToMap(mapRef.current, 'chevron', `${rootUrl}/images/Chevron.png`, {
    // SDF format enables colorization:
    sdf: true,
  });
  // Manually add the default attribution control so we have a reference to it.
  const attributionControl = new mapboxgl.AttributionControl();
  setAttributionControl(
    mapRef.current,
    attributionControl,
    attributionControlRef,
  );
};

const onMapUnload = (attributionControlRef) => () => {
  const controlRef = attributionControlRef;
  controlRef.current = null;
};

const FieldMap = ({ readonly }) => {
  const { data: queryData, loading } = useQueryWithFarmId(FarmQuery);
  const [farmLng, farmLat] =
    queryData?.farm.mapCenter?.geometry?.coordinates || [];
  const mapContainer = useRef(null);
  const [lng, setLng] = useState(farmLng || 0);
  const [lat, setLat] = useState(farmLat || 0);

  const {
    crops: { availableCrops },
    fields: { fieldSections },
    mapState: {
      isActiveField,
      activeLayerId,
      mapMode,
      context: { keylineMode = false, hasRotated = false, hasOffset = false },
      mapRef,
      attributionControlRef,
      mapLoaded,
      setMapLoaded,
      zoom,
      setZoom,
    },
    layers: { layers },
    soilAttributes: { colorKey },
  } = useContext(MapContext);

  useEffect(() => {
    if (farmLat === undefined || farmLng === undefined) {
      return;
    }
    if (mapRef.current) {
      // lng and lat are updated by move event handler
      mapRef.current.setCenter([farmLng, farmLat]);
    } else {
      setLng(farmLng);
      setLat(farmLat);
    }
  }, [farmLat, farmLng, mapRef]);

  useInitializeMapEffect({
    mapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    lat,
    setLat,
    lng,
    setLng,
    zoom,
    setZoom,
    onLoad: onMapLoad(attributionControlRef),
    onUnload: onMapUnload(attributionControlRef),
    preserveDrawingBuffer: true, // Needed to generate data url from canvas
    attributionControl: false, // Add attribution manually so we can store a ref
  });

  useEffect(() => {
    if (loading) return;
    // If farm has no mapCenter, then default zoom to show full Earth
    if ((farmLat == null || farmLng == null) && mapRef.current) {
      mapRef.current.setCenter(CONUS_CENTER);
      mapRef.current.setZoom(EARTH_ZOOM);
    }
  }, [farmLat, farmLng, loading, mapRef]);

  useDrawLayer(mapRef.current);
  useDraw(mapRef.current);
  useMeasurement(mapRef.current);
  useRotate(mapRef.current);
  useOffset(mapRef.current);

  const debug = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('debug') === 'points';
  }, []);

  const handleFullscreenShortcut = useCallback(
    (evt) => {
      const map = mapRef.current;
      if (!map) return;

      // Toggle fullscreen state
      toggleFullscreen(map, evt);

      // Manually trigger keyup event since react-hotkeys-hook has fullscreen issues. See:
      // - https://github.com/JohannesKlauss/react-hotkeys-hook/issues/1067
      // - https://github.com/JohannesKlauss/react-hotkeys-hook/issues/1074
      const clearKey = (evtSpec) =>
        document.dispatchEvent(new KeyboardEvent('keyup', evtSpec));
      window.addEventListener(
        'fullscreenchange',
        () => {
          clearKey({ key: 'f', code: 'KeyF' });
          clearKey({ key: 'ƒ', code: 'KeyF' }); // ƒ = alt+f
        },
        { once: true },
      );
    },
    [mapRef],
  );
  const soilLayer = useColorSoilLayer({
    colorKey,
    soilLayer: layers[SOIL_BOUNDARIES_LAYER_ID],
  });

  return (
    <>
      <MapContainer ref={mapContainer} className="h-100 w-100" />
      {mapLoaded &&
        Object.values(layers).map((layer) => {
          // Hide layer if we are currently drawing/editing it.
          const hideLayer =
            [
              mapModes.DRAW_LAYER,
              mapModes.EDIT_LAYER,
              mapModes.EDIT_LAYER_FEATURE,
            ].includes(mapMode) && layer.layerId === activeLayerId;
          return (
            !hideLayer && (
              <UserLayer
                key={layer.layerId}
                map={mapRef.current}
                layer={
                  layer.layerId === SOIL_BOUNDARIES_LAYER_ID ? soilLayer : layer
                }
              />
            )
          );
        })}
      {mapLoaded &&
        fieldSections.map((section) => {
          // Don't show the active field if we are drawing it.
          const isActiveDrawing =
            isActiveField(section) && mapMode === mapModes.DRAW && !keylineMode;
          const hideField = !section.shown || isActiveDrawing;
          return (
            !hideField &&
            section.fieldFeature && (
              <FieldSectionLayers
                key={section.id}
                section={section}
                map={mapRef.current}
                showRotate={
                  isActiveField(section) &&
                  mapMode === mapModes.ROTATE &&
                  !hasRotated
                }
                showOffset={
                  isActiveField(section) &&
                  mapMode === mapModes.OFFSET &&
                  !hasOffset
                }
                showCenter={
                  isActiveField(section) &&
                  mapMode === mapModes.ROTATE &&
                  hasRotated
                }
                readonly={readonly}
                debug={debug}
                cropMap={availableCrops}
              />
            )
          );
        })}
      {mapLoaded && <TilesetLayer map={mapRef.current} />}
      {mapLoaded && <NaipLayer map={mapRef.current} />}
      {mapLoaded && <AzureLayer map={mapRef.current} />}
      {mapLoaded && <NoSatelliteLayer map={mapRef.current} />}
      {mapLoaded && <ContourLayer map={mapRef.current} />}
      {mapLoaded && <BaseMap map={mapRef.current} />}
      {mapLoaded && (
        <Shortcut
          shortcut={SHORTCUTS.TOGGLE_MAP_FULLSCREEN.key}
          handler={handleFullscreenShortcut}
        />
      )}
      <CursorLatLng mapRef={mapRef} mapLoaded={mapLoaded} />
    </>
  );
};

export default FieldMap;
